import { WriterSearch } from '@App/stores/search/models/writer-search'
import { PaginationBody } from '@Entities/pagination'
import { searchWorks, searchWriters } from '@App/api/routes/search'
import { SearchResultPartyDetails } from '@Entities/party-details'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { v4 as uuidv4 } from 'uuid'
import getWriterNameAutoCompleteSuggestions from '@App/helpers/search/get-writer-name-auto-complete-suggestions'
import { Works } from '@Entities/work/works'
import { WriterModelWithWorksCount } from '@Entities/work/writer-model-with-works-ount'
import PublicSearchPartyStore from '@App/stores/search/public/public-search-party-store'
import { PartyPage } from '@App/stores/search/party/query-party-store'

const searchWritersOptions: Array<SelectOptionHide<WriterSearch>> = [
    {
        id: uuidv4(),
        value: 'fullName',
        label: 'Writer Name',
        autoCompleteFetcher: getWriterNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'ipiNameNumber', label: 'Writer IPI' },
]

export const DEFAULT_SEARCH_BY_WRITER_CRITERIA: Array<SearchCriterion<WriterSearch>> = [
    { id: searchWritersOptions[0].id, selectedOption: searchWritersOptions[0], searchTerm: '', isFixed: true },
]

export default class PublicSearchWritersStore extends PublicSearchPartyStore<WriterSearch> {
    readonly partySearchScope = 'writer'

    protected readonly partyLabel = 'Writer'

    createWriterSearchCriteriaStore(): SearchCriteriaStore<WriterSearch> {
        const writerSearchMaxCriteria = 1
        return new SearchCriteriaStore(
            DEFAULT_SEARCH_BY_WRITER_CRITERIA,
            searchWritersOptions,
            SearchOptionType.filter,
            RequiredCriteria.first,
            writerSearchMaxCriteria
        )
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createWriterSearchCriteriaStore()
    }

    get writerSearch(): WriterSearch {
        return this.partyQuery
    }

    protected async searchForPartiesApiCall(
        pagination: PaginationBody,
        query: WriterSearch
    ): Promise<PartyPage<SearchResultPartyDetails>> {
        const writersSearchModel = await searchWriters(pagination, query)
        return {
            parties: writersSearchModel.data.content.map((writer) =>
                PublicSearchWritersStore.convertWriterToPartyDetails(writer)
            ),
            count: writersSearchModel.data.totalElements,
        }
    }

    protected async searchWorksForParty(party: SearchResultPartyDetails, pagination: PaginationBody): Promise<Works> {
        const apiResponse = await searchWorks(pagination, { writerIpIds: party.ipId ?? undefined })
        return { count: apiResponse.data.totalElements, works: apiResponse.data.content }
    }

    static convertWriterToPartyDetails(writerModel: WriterModelWithWorksCount): SearchResultPartyDetails {
        return {
            ipId: writerModel.ipId,
            ipiNumber: writerModel.ipiNumber,
            worksCount: writerModel.worksCount,
            name: [writerModel.firstName, writerModel.lastName].filter(Boolean).join(' '),
        }
    }
}
